/**
 * Aufgaben-Gruppierung und Fristformulierung für die Oberfläche (Paket 10).
 *
 * In v3 berechnete der Server die Gruppe. Hier: reine Funktion — ohne DB
 * testbar, ohne API-Erweiterung, für Oberfläche, MCP und Metarr gleich.
 *
 * Rechnet in der Zeitzone der Person, nicht in UTC.
 */
import { heuteEnde, heuteStart } from "./zeitzone.js";
export const AUFGABEN_GRUPPEN = [
    "ueberfaellig",
    "heute",
    "diese_woche",
    "spaeter",
    "ohne_datum",
];
function alsDatum(wert) {
    if (wert == null)
        return null;
    if (wert instanceof Date)
        return Number.isNaN(wert.getTime()) ? null : wert;
    const d = new Date(wert);
    return Number.isNaN(d.getTime()) ? null : d;
}
/** Ende der aktuellen Kalenderwoche (Montag–Sonntag, exklusives Ende). */
function wocheEnde(zeitzone, jetzt) {
    const start = heuteStart(zeitzone, jetzt);
    const tagFmt = new Intl.DateTimeFormat("en-US", {
        timeZone: zeitzone,
        weekday: "short",
    });
    const wd = tagFmt.format(jetzt);
    const map = {
        Sun: 0,
        Mon: 1,
        Tue: 2,
        Wed: 3,
        Thu: 4,
        Fri: 5,
        Sat: 6,
    };
    const day = map[wd] ?? 1;
    // Tage bis einschließlich Sonntag, dann +1 Tag als exklusive Grenze
    const bisSonntag = day === 0 ? 0 : 7 - day;
    return new Date(start.getTime() + (bisSonntag + 1) * 24 * 60 * 60 * 1000);
}
/**
 * Fünf Gruppen in fester Reihenfolge: ueberfaellig → heute → diese_woche →
 * spaeter → ohne_datum.
 */
export function gruppeVon(aufgabe, zeitzone, jetzt = new Date()) {
    if (aufgabe.erledigt)
        return "ohne_datum";
    const faellig = alsDatum(aufgabe.faelligAm);
    if (!faellig)
        return "ohne_datum";
    const start = heuteStart(zeitzone, jetzt).getTime();
    const ende = heuteEnde(zeitzone, jetzt).getTime();
    const t = faellig.getTime();
    if (t < start)
        return "ueberfaellig";
    if (t >= start && t < ende)
        return "heute";
    if (t < wocheEnde(zeitzone, jetzt).getTime())
        return "diese_woche";
    return "spaeter";
}
function tageDifferenz(von, bis, zeitzone) {
    const a = heuteStart(zeitzone, von).getTime();
    const b = heuteStart(zeitzone, bis).getTime();
    return Math.round((a - b) / (24 * 60 * 60 * 1000));
}
/**
 * Fristtext je Gruppe — überfällig zeigt Verstrichenes, nicht das Datum.
 */
export function formuliereFrist(aufgabe, gruppe, zeitzone, jetzt = new Date()) {
    if (aufgabe.erledigt)
        return "erledigt";
    const faellig = alsDatum(aufgabe.faelligAm);
    if (!faellig || gruppe === "ohne_datum")
        return "";
    if (gruppe === "ueberfaellig") {
        const tage = Math.max(1, tageDifferenz(jetzt, faellig, zeitzone));
        return `seit ${tage} T`;
    }
    if (gruppe === "heute")
        return "heute";
    const fmtKurz = new Intl.DateTimeFormat("de-DE", {
        timeZone: zeitzone,
        day: "2-digit",
        month: "2-digit",
    });
    if (gruppe === "diese_woche") {
        const wd = new Intl.DateTimeFormat("de-DE", {
            timeZone: zeitzone,
            weekday: "short",
        }).format(faellig);
        return `${wd} ${fmtKurz.format(faellig)}`;
    }
    // spaeter
    return fmtKurz.format(faellig);
}
/**
 * Wann etwas zuletzt bewegt wurde — der Rückblick zu `formuliereFrist`.
 *
 * Dieselbe knappe Registersprache in derselben Zeitzone: „gerade", „vor
 * 20 min", „vor 3 h", „gestern", „vor 4 T", darüber der Kalendertag. Ab einer
 * Woche zählt niemand mehr Tage zurück; dann ist das Datum die genauere
 * Auskunft und zugleich die kürzere.
 *
 * ── Uhr oder Kalender ─────────────────────────────────────────────────────
 *
 * Die Tagesstufen rechnen in Kalendertagen und nicht in 24-Stunden-Schritten,
 * genau wie `gruppeVon`: Um 00:30 heißt eine Änderung von gestern 23:00
 * „gestern", nicht „vor 2 h". So wird darüber gesprochen.
 *
 * **Unter einer Stunde gilt aber die Uhr**, sonst kippt derselbe Grundsatz ins
 * Absurde: Um 00:01 wäre eine Änderung von 23:59 sonst „gestern" — richtig
 * gerechnet und trotzdem unbrauchbar, weil sie neunzig Sekunden her ist.
 *
 * Der längste Text ist zehn Zeichen („vor 59 min"); die Spalte im Entwurf ist
 * darauf ausgelegt.
 */
export function formuliereBewegt(bewegtAm, zeitzone, jetzt = new Date()) {
    const bewegt = alsDatum(bewegtAm);
    if (!bewegt)
        return "";
    const verstrichen = jetzt.getTime() - bewegt.getTime();
    // Auch eine Uhr, die vorgeht, sagt „gerade" statt „vor -3 min".
    if (verstrichen < 60_000)
        return "gerade";
    const minuten = Math.floor(verstrichen / 60_000);
    if (minuten < 60)
        return `vor ${minuten} min`;
    const tage = tageDifferenz(jetzt, bewegt, zeitzone);
    if (tage <= 0)
        return `vor ${Math.floor(minuten / 60)} h`;
    if (tage === 1)
        return "gestern";
    if (tage < 7)
        return `vor ${tage} T`;
    return new Intl.DateTimeFormat("de-DE", {
        timeZone: zeitzone,
        day: "2-digit",
        month: "2-digit",
    }).format(bewegt);
}
//# sourceMappingURL=aufgaben-sicht.js.map